package app.nestbridge.feature.children.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.tooling.preview.PreviewParameter
import com.karawek.core.ui.components.CoreCard
import com.karawek.core.ui.components.CoreDivider
import com.karawek.core.ui.components.CoreLoadingIndicator
import com.karawek.core.ui.components.CorePrimaryButton
import com.karawek.core.ui.components.CoreScaffold
import com.karawek.core.ui.components.CoreSecondaryButton
import com.karawek.core.ui.components.CoreTag
import com.karawek.core.ui.components.CoreTagTone
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTheme
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.ThemeMode
import com.karawek.core.ui.theme.ThemeModeProvider
import app.nestbridge.feature.children.model.Child
import app.nestbridge.feature.children.model.ChildStatus
import app.nestbridge.feature.children.screen.ChildListEvent
import app.nestbridge.feature.children.screen.ChildListScreen
import app.nestbridge.feature.children.screen.ChildListState
import app.nestbridge.ui.theme.NestBridgeTheme
import com.slack.circuit.codegen.annotations.CircuitInject
import kotlinx.datetime.LocalDate
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import kotlin.time.Clock

@CircuitInject(
    screen = ChildListScreen::class,
    scope = AppScope::class,
)
@Composable
fun ChildListUi(
    state: ChildListState,
    modifier: Modifier = Modifier,
) {
    CoreScaffold(
        title = "Your Children",
        subtitle = "View the status of your children's care teams and manage their profiles.",
        footer = { Footer(state) },
        onAppear = { state.eventSink(ChildListEvent.OnAppear) },
        modifier = modifier,
    ) {
        if (state.isLoading) {
            CoreLoadingIndicator()
        } else {
                    if (state.children.isEmpty()) {
                        Text(
                            text = "No children added yet. Add your first child to get started.",
                            style = CoreTypography.body,
                            color = CoreTheme.colors.textSecondary,
                        )
                    } else {
                        Column(
                            verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
                        ) {
                            state.children.forEach { child ->
                                CoreCard(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .clickable { state.eventSink(ChildListEvent.SelectChild(child.id)) },
                                ) {
                                    Row(
                                        modifier = Modifier
                                            .fillMaxWidth()
                                            .padding(CoreSpacing.xs),
                                        horizontalArrangement = Arrangement.SpaceBetween,
                                        verticalAlignment = Alignment.CenterVertically,
                                    ) {
                                        Column(
                                            modifier = Modifier.weight(1f),
                                            verticalArrangement = Arrangement.spacedBy(CoreSpacing.xs),
                                        ) {
                                            Text(
                                                text = child.effectiveDisplayName,
                                                style = CoreTypography.title,
                                                color = CoreTheme.colors.textPrimary,
                                            )
                                            Text(
                                                text = child.formatedDateOfBirth,
                                                style = CoreTypography.bodySecondary,
                                                color = CoreTheme.colors.textSecondary,
                                            )
                                        }
                                        
                                        Row(
                                            horizontalArrangement = Arrangement.spacedBy(CoreSpacing.xs),
                                            verticalAlignment = Alignment.CenterVertically,
                                        ) {
                                            val teamCount = state.childTeamCounts[child.id] ?: 0
                                            val hasPendingInvitation = state.childPendingInvitations[child.id] ?: false

                                            if (teamCount == 0) {
                                                if (hasPendingInvitation) {
                                                    CoreTag(
                                                        text = "Invitation Sent",
                                                        tone = CoreTagTone.Warning,
                                                    )
                                                } else {
                                                    CoreTag(
                                                        text = "New",
                                                        tone = CoreTagTone.Info,
                                                    )
                                                }
                                            }

                                            Text(
                                                text = "›",
                                                style = CoreTypography.title,
                                                color = CoreTheme.colors.textSecondary,
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }
        }
    }
}

@Composable
private fun Footer(state: ChildListState) =
    Column(
        verticalArrangement = Arrangement.spacedBy(CoreSpacing.md),
    ) {
        if (state.hasAnyChildWithTeam) {
            CorePrimaryButton(
                text = "Continue",
                onClick = { state.eventSink(ChildListEvent.Skip) },
                modifier = Modifier
                    .fillMaxWidth(),
            )
        }

        CoreSecondaryButton(
            text = "+ Add Another Child",
            onClick = { state.eventSink(ChildListEvent.AddChild) },
            modifier = Modifier
                .fillMaxWidth(),
        )

        CoreDivider()

        Column(
            verticalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
        ) {
            Text(
                text = "Have a QR code? Scan it to review an invitation.",
                style = CoreTypography.footnote,
                color = CoreTheme.colors.textSecondary,
            )

            CoreSecondaryButton(
                text = "Scan QR code",
                onClick = { state.eventSink(ChildListEvent.ScanQRCode) },
                modifier = Modifier
                    .fillMaxWidth(),
            )
        }
    }

@Preview
@Composable
private fun ChildListUiWithContinuePreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        ChildListUi(
            state = ChildListState(
                children = listOf(
                    Child(
                        id = "child-1",
                        fullName = "Sofia Grace Chen",
                        displayName = "Sofia",
                        dateOfBirth = LocalDate(2023, 1, 21),
                        primaryLanguages = emptyList(),
                        stewardUserIds = listOf("user-1"),
                        status = ChildStatus.Active,
                        createdAt = Clock.System.now(),
                        createdBy = "user-1",
                        modifiedAt = Clock.System.now(),
                        modifiedBy = "user-1",
                    ),
                    Child(
                        id = "child-2",
                        fullName = "Liam James Walker",
                        displayName = "Liam",
                        dateOfBirth = LocalDate(2022, 9, 8),
                        primaryLanguages = emptyList(),
                        stewardUserIds = listOf("user-2"),
                        status = ChildStatus.Active,
                        createdAt = Clock.System.now(),
                        createdBy = "user-2",
                        modifiedAt = Clock.System.now(),
                        modifiedBy = "user-2",
                    ),
                    Child(
                        id = "child-3",
                        fullName = "Amara Elise Patel",
                        displayName = "Amara",
                        dateOfBirth = LocalDate(2021, 4, 3),
                        primaryLanguages = emptyList(),
                        stewardUserIds = listOf("user-3"),
                        status = ChildStatus.Active,
                        createdAt = Clock.System.now(),
                        createdBy = "user-3",
                        modifiedAt = Clock.System.now(),
                        modifiedBy = "user-3",
                    ),
                ),
                childTeamCounts = mapOf(
                    "child-1" to 0,
                    "child-2" to 1,
                    "child-3" to 0,
                ),
                childPendingInvitations = mapOf(
                    "child-1" to false,
                    "child-2" to false,
                    "child-3" to true,
                ),
                hasAnyChildWithTeam = true,
                isLoading = false,
                eventSink = {},
            ),
        )
    }
}

@Preview
@Composable
private fun ChildListUiPreview(
    @PreviewParameter(ThemeModeProvider::class) themeMode: ThemeMode,
) {
    NestBridgeTheme(themeMode = themeMode) {
        ChildListUi(
            state = ChildListState(
                children = listOf(
                    Child(
                        id = "child-1",
                        fullName = "Sofia Grace Chen",
                        displayName = "Sofia",
                        dateOfBirth = LocalDate(2023, 1, 21),
                        primaryLanguages = emptyList(),
                        stewardUserIds = listOf("user-1"),
                        status = ChildStatus.Active,
                        createdAt = Clock.System.now(),
                        createdBy = "user-1",
                        modifiedAt = Clock.System.now(),
                        modifiedBy = "user-1",
                    ),
                    Child(
                        id = "child-2",
                        fullName = "Liam James Walker",
                        displayName = "Liam",
                        dateOfBirth = LocalDate(2022, 9, 8),
                        primaryLanguages = emptyList(),
                        stewardUserIds = listOf("user-2"),
                        status = ChildStatus.Active,
                        createdAt = Clock.System.now(),
                        createdBy = "user-2",
                        modifiedAt = Clock.System.now(),
                        modifiedBy = "user-2",
                    ),
                ),
                childTeamCounts = mapOf(
                    "child-1" to 0,
                    "child-2" to 0,
                ),
                childPendingInvitations = mapOf(
                    "child-1" to false,
                    "child-2" to false,
                ),
                hasAnyChildWithTeam = false,
                isLoading = false,
                eventSink = {},
            ),
        )
    }
}
